import React from "react";
import DataGrid, { Column, Editing, Export, FilterPanel, GroupPanel, Lookup, Pager, Paging, Scrolling, SearchPanel, Sorting } from "devextreme-react/data-grid";
import { Button, LoadIndicator, LoadPanel } from "devextreme-react";
import lookupService, { LookupTypeIndexes, LookupTypeItem } from "../../services/LookupService";
import { onEditCanceledEvent, onExportingEvent } from "../../types/DevExtremeTypes";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";

interface ViewBrandsGridProps {
    clientId?: string;
    clientLookup: LookupTypeItem[];
    refreshSignal?: boolean;
}

interface Brand {
    id: string;
    value: string;
    businessEntityId: string;
}

interface ViewBrandsGridState {
    brands: Brand[];
    clients: LookupTypeItem[];
    showProgressIndicator: boolean;
    loadPanelVisible: boolean;
    errorMessage: any[];
}

export default class ViewBrandsGrid extends React.Component<ViewBrandsGridProps, ViewBrandsGridState> {
    private readonly lookupService = new lookupService();
    private readonly gridRef = React.createRef<any>();

    constructor(props: ViewBrandsGridProps) {
        super(props);
        this.state = { brands: [], clients: [], showProgressIndicator: false, loadPanelVisible: false, errorMessage: [] };
    }

    componentDidUpdate(previousProps: ViewBrandsGridProps) {
        if (previousProps.clientLookup !== this.props.clientLookup) {
            this.setState({ clients: this.props.clientLookup });
        }
        if (previousProps.clientId !== this.props.clientId) {
            this.loadBrands(this.props.clientId || "");
        } else if (previousProps.refreshSignal !== this.props.refreshSignal && this.props.clientId) {
            this.loadBrands(this.props.clientId);
        }
    }

    loadBrands = (businessEntityId: string) => {
        if (!businessEntityId) {
            this.setState({ brands: [], showProgressIndicator: false });
            return;
        }
        this.setState({ showProgressIndicator: true });
        this.lookupService.getBrandsByBusinessEntityId(businessEntityId)
            .then((response) => this.setState({ brands: response.data?.data || response.data || [], showProgressIndicator: false }))
            .catch(this.handleError);
    };

    handleError = (error: any) => {
        this.setState({
            showProgressIndicator: false,
            loadPanelVisible: false,
            errorMessage: error?.response?.data?.error || [{ columnName: "Brand", errorMessage: "Unable to load brands." }],
        });
    };

    getValidationErrors = (error: any): any[] => {
        const responseData = error?.response?.data;
        return responseData?.validationErrors || responseData?.error || [{ columnName: "Brand", errorMessage: "Unable to save the brand." }];
    };

    getBusinessEntityId = (row: Brand, data: any): string => data?.businessEntityId || row?.businessEntityId || "";

    onRowInserting = (e: any) => {
        this.setState({ loadPanelVisible: true, errorMessage: [] });
        this.lookupService.saveBrand({ id: "", value: e.data?.value || "", businessEntityId: e.data?.businessEntityId || "" })
            .then(() => this.loadBrands(e.data?.businessEntityId || ""))
            .catch((error) => { e.cancel = true; this.setState({ errorMessage: this.getValidationErrors(error) }); })
            .finally(() => this.setState({ loadPanelVisible: false }));
    };

    onRowUpdating = (e: any) => {
        const brand = e.oldData as Brand;
        const data = { id: brand.id, value: e.newData.value ?? brand.value, businessEntityId: this.getBusinessEntityId(brand, e.newData) };
        this.setState({ loadPanelVisible: true, errorMessage: [] });
        this.lookupService.saveBrand(data)
            .then(() => this.loadBrands(data.businessEntityId))
            .catch((error) => { e.cancel = true; this.setState({ errorMessage: this.getValidationErrors(error) }); })
            .finally(() => this.setState({ loadPanelVisible: false }));
    };

    onRowRemoving = (e: any) => {
        this.setState({ loadPanelVisible: true, errorMessage: [] });
        this.lookupService.deleteBrand((e.data as Brand).id)
            .then(() => this.loadBrands((e.data as Brand).businessEntityId))
            .catch((error) => { e.cancel = true; this.setState({ errorMessage: this.getValidationErrors(error) }); })
            .finally(() => this.setState({ loadPanelVisible: false }));
    };

    onEditCancelled = (_e: onEditCanceledEvent) => this.setState({ errorMessage: [] });

    addBrand = () => {
        this.gridRef.current?.instance?.addRow();
    };

    onExporting = (e: onExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Brands");
        exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Brands.xlsx"));
        });
        e.cancel = true;
    };

    renderErrorMessage = () => this.state.errorMessage?.length ? <span className="unscheduled-shift"><ul>{this.state.errorMessage.map((item: any, index: number) => <li key={index}>{item.columnName}: {item.errorMessage || item}</li>)}</ul></span> : null;

    render() {
        return <div>
            {this.renderErrorMessage()}
            {this.state.showProgressIndicator ? <div className="starter-template"><LoadIndicator id="brands-grid-indicator" height={100} width={100} visible={true} /></div> : <>
                <LoadPanel shadingColor="rgba(0,0,0,0.4)" visible={this.state.loadPanelVisible} />
                <div className="view-clients-style-grid">
                <DataGrid ref={this.gridRef} dataSource={this.state.brands} showBorders={false} showColumnLines={false} hoverStateEnabled={true} columnAutoWidth={true}
                    allowColumnReordering={true} allowColumnResizing={true} columnResizingMode="widget" columnMinWidth={100}
                    keyExpr="id" onExporting={this.onExporting}
                    onRowInserting={this.onRowInserting} onRowUpdating={this.onRowUpdating} onRowRemoving={this.onRowRemoving} onEditCanceling={this.onEditCancelled}>
                    <SearchPanel visible={true} placeholder="Search" />
                    <GroupPanel visible={true} />
                    <FilterPanel visible={true} />
                    <Scrolling useNative={true} showScrollbar="always" />
                    <Export enabled={true} />
                    <Sorting mode="single" />
                    <Paging defaultPageSize={10} />
                    <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                    <Editing mode="row" useIcons={true} allowAdding={true} allowUpdating={true} allowDeleting={true} />
                    <Column type="buttons" buttons={["edit", "delete"]} />
                    <Column dataField="id" caption="BRAND ID" allowEditing={false} />
                    <Column dataField="value" caption="BRAND NAME" />
                    <Column dataField="businessEntityId" caption="CLIENT ID">
                        <Lookup dataSource={this.state.clients} displayExpr="value" valueExpr="id" />
                    </Column>
                </DataGrid>
                </div>
            </>}
        </div>;
    }
}
